// The viewer, and which of Nebari's two modes they are looking at.
//
// Replaces the persona hook. Persona was DERIVED — role names collapsed to one
// of four chrome variants, and what each variant saw was largely whatever
// survived filtering. Mode is GRANTED: `access:admin_mode` and
// `access:teacher_mode` say, in the tenant config, what a viewer may enter.
//
// The URL is the authority while the viewer is on it. A link straight into the
// other mode's tree switches modes and remembers the switch, so a reload lands
// where the viewer actually was. Entitlement is re-derived every render and
// gates both the URL and the stored value, so a grant lost since the last
// switch narrows the viewer rather than being trusted.
//
// Nothing here waits on `/staff/me`. The profile supplies display fields only
// (name, initials, regions); a failed or slow fetch degrades those and leaves
// the navigation rendered.
//
// `profileLoading` and `profileFailed` are reported SEPARATELY for the callers
// that cannot degrade. The profile is fetched with the very token this hook
// decodes, so it always lands strictly after it, and a one-shot `<Navigate
// replace>` computed in between routes a viewer by regions they do not have
// yet and then unmounts. `profileFailed` is the second half of that: a request
// that FAILS also settles, and settles as "no regions", which is a different
// fact about the same viewer.

import { useEffect, useSyncExternalStore } from 'react';
import { useLocation } from 'react-router-dom';

import type { CurrentStaff } from '../../api/staff';
import { EMAIL_CLAIM } from '../../auth/jwt-claims';
import { useCurrentUser } from '../../contexts/CurrentUserContext';
import { useTokenClaims } from '../../hooks/use-token-claims';
import { projectRegions } from '../../hooks/use-viewer-regions';
import { usePermissions } from '../../hooks/usePermissions';

import {
  getStoredViewMode,
  modeFromPath,
  modeLabel,
  modesAvailableTo,
  setStoredViewMode,
  subscribeViewMode,
  type ViewMode,
} from './viewMode';

export interface AppModeInfo {
  /** The mode the chrome, the nav and the routing all follow. */
  mode: ViewMode;
  /**
   * The modes this viewer may enter, in switcher order.
   *
   * One entry means there is nothing to switch to and the control hides
   * itself. EMPTY means there is no workspace at all — a token minted before
   * the mode scopes existed, most often — and `mode` is then only what the
   * chrome renders, not somewhere the viewer may go.
   */
  availableModes: ViewMode[];
  /** Full name from the staff profile if available, falling back to email. */
  displayName: string;
  /** Two-letter initials for the avatar. */
  initials: string;
  /** "Admin · National" / "Teacher · PNW" — the mode, then the scope it is
   *  exercised over. */
  scopeLabel: string;
  /** Email for the avatar popover. */
  email: string | null;
  /** Region codes the viewer is scoped to. Empty for an account scoped to
   *  everything. */
  regionCodes: string[];
  /** Region names parallel to regionCodes. */
  regionNames: string[];
  /**
   * Region UUIDs the viewer is scoped to.
   *
   * Separate from `regionCodes` because the codes are for DISPLAY while the
   * ids are what the region and workshop APIs filter on — and a region may
   * carry no code at all, so the two lists are not interchangeable.
   */
  regionIds: string[];
  /** True only until the ACCESS TOKEN has decoded. Deliberately NOT tied to
   *  the profile fetch — see the module note. */
  loading: boolean;
  /**
   * True until `/staff/me` has settled, one way or the other.
   *
   * Separate from {@link loading} because the distinction is real: chrome can
   * render on the token alone and fill its display fields in afterwards, but a
   * ONE-SHOT `<Navigate replace>` cannot — it unmounts on arrival, so a
   * redirect computed before `regionNames` exists is final.
   */
  profileLoading: boolean;
  /**
   * There is no profile, because `/staff/me` failed — after the single retry
   * `CurrentUserProvider` allows it.
   *
   * Without this the two outcomes are the same value: a failure and a viewer
   * who genuinely holds no regions both arrive as `profileLoading: false` with
   * an empty `regionNames`.
   *
   * A failed REFRESH over a profile already in hand leaves this false: those
   * regions are still the viewer's own, and demoting them on a later blip
   * would be the same wrong answer in the other direction.
   */
  profileFailed: boolean;
}

const LOADING: AppModeInfo = {
  mode: 'teacher',
  availableModes: [],
  displayName: 'Loading…',
  initials: '–',
  scopeLabel: '',
  email: null,
  regionCodes: [],
  regionNames: [],
  regionIds: [],
  loading: true,
  profileLoading: true,
  profileFailed: false,
};

/** The profile is unavailable, not merely empty: `/staff/me` errored AND left
 *  nothing behind. */
function profileUnavailable(staff: CurrentStaff | null, error: Error | null): boolean {
  return error !== null && staff === null;
}

interface ViewerIdentity {
  displayName: string;
  initials: string;
  email: string | null;
}

/**
 * The fields the avatar shows. These are the ONLY things the `/staff/me`
 * profile is needed for, so they degrade to the token's own email claim when
 * it is missing rather than taking the navigation down with them.
 */
function deriveIdentity(staff: CurrentStaff | null, tokenEmail: string | null): ViewerIdentity {
  const email = staff?.email ?? tokenEmail;
  const names = [staff?.firstName, staff?.lastName].filter((n): n is string => !!n);
  const displayName = names.join(' ') || email || 'Nebari user';
  const initials = names.length
    ? names
        .map((n) => n[0])
        .join('')
        .toUpperCase()
    : (email?.slice(0, 2).toUpperCase() ?? '–');
  return { displayName, initials, email };
}

/** "Admin · National", "Teacher · PNW". With no regions there is no scope to
 *  name, so admin mode says National — it reads every region — and teacher
 *  mode says nothing rather than filling the slot with the mode again. */
function deriveScopeLabel(mode: ViewMode, regionNames: string[]): string {
  const label = modeLabel(mode);
  if (mode === 'teacher') return regionNames[0] ? `${label} · ${regionNames[0]}` : label;
  return regionNames.length ? `${label} · ${regionNames.join(' · ')}` : `${label} · National`;
}

/**
 * The mode in force: the URL first, then the remembered choice.
 *
 * The URL wins because a deep link into the other tree is the viewer saying
 * where they want to be, and the chrome has to agree with the page under it.
 * Both are filtered through `available`, so neither can widen anyone.
 *
 * With `available` EMPTY there is no mode to be in. It still answers
 * `'teacher'` — the chrome has to render something, and the narrower of the
 * two is the safe thing to render — but nothing routes off this: callers read
 * `availableModes` for that, because an empty set is a different state from
 * "in teacher mode".
 */
function resolveMode(pathname: string, available: ViewMode[], stored: ViewMode | null): ViewMode {
  const fromPath = modeFromPath(pathname);
  if (fromPath && available.includes(fromPath)) return fromPath;
  if (stored && available.includes(stored)) return stored;
  return available[0] ?? 'teacher';
}

/** Resolve the current viewer and the mode they are in. */
export function useAppMode(): AppModeInfo {
  const { currentUser, loading: profileLoading, error: profileError } = useCurrentUser();
  const { claims, loading: tokenLoading } = useTokenClaims();
  const { hasPermission } = usePermissions();
  const { pathname } = useLocation();
  // Subscribed rather than read directly so every consumer (shell, panel,
  // bottom bar, HomeRedirect) re-renders together when the mode is switched.
  const stored = useSyncExternalStore(subscribeViewMode, getStoredViewMode);

  const availableModes = modesAvailableTo(hasPermission);
  const entitled = availableModes.length > 0;
  const mode = resolveMode(pathname, availableModes, stored);

  // Arriving by URL is a switch, and a switch is remembered — otherwise the
  // next reload of `/` would send the viewer back to the mode they left.
  // `setStoredViewMode` no-ops when the value is unchanged, so this settles
  // after one pass rather than looping. A viewer entitled to nothing stores
  // nothing: there was no switch to remember.
  useEffect(() => {
    if (!tokenLoading && entitled) setStoredViewMode(mode);
  }, [mode, tokenLoading, entitled]);

  if (tokenLoading) return LOADING;

  const { regionCodes, regionNames, regionIds } = projectRegions(currentUser);
  const identity = deriveIdentity(currentUser, (claims?.[EMAIL_CLAIM] as string) || null);

  return {
    mode,
    availableModes,
    ...identity,
    scopeLabel: deriveScopeLabel(mode, regionNames),
    regionCodes,
    regionNames,
    regionIds,
    loading: false,
    profileLoading,
    profileFailed: profileUnavailable(currentUser, profileError),
  };
}
